// src/components/layouts/AdminLayout.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaTags,
  FaBoxOpen,
  FaClipboardList,
  FaTruck,
  FaChartBar,
  FaBars,
  FaSignOutAlt,
  FaFolderOpen,
  FaBook,
  FaSyringe,
  FaChevronDown,
  FaArrowDown,
  FaClipboardCheck,
  FaKey,
} from "react-icons/fa";

import logoKemenkes from "../../assets/img/logo/logokk.png";
import logoSipedas from "../../assets/img/logo/logosipedas.png";

export default function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sideOpen, setSideOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const activeKey = useMemo(() => {
    if (pathname.startsWith("/admin/users")) return "users";
    if (pathname.startsWith("/admin/categories")) return "categories";
    if (pathname.startsWith("/admin/products")) return "products";

    if (pathname.startsWith("/admin/produk-masuk")) return "incoming";
    if (pathname.startsWith("/admin/laporan-persediaan")) return "inventory";

    if (pathname.startsWith("/admin/submissions")) return "submissions";
    if (pathname.startsWith("/admin/pengiriman")) return "shipping";
    if (pathname.startsWith("/admin/reports")) return "reports";

    if (pathname.startsWith("/admin/change-password")) return "change_password";

    if (pathname.startsWith("/admin/log")) return "log";
    if (pathname === "/admin") return "submissions";
    return "";
  }, [pathname]);

  useEffect(() => {
    if (pathname.startsWith("/admin/log")) setLogOpen(true);
  }, [pathname]);

  const headerTitle = useMemo(() => {
    switch (activeKey) {
      case "users":
        return "Manajemen Pengguna";
      case "categories":
        return "Manajemen Kategori";
      case "products":
        return "Manajemen Produk";

      case "incoming":
        if (pathname.startsWith("/admin/produk-masuk/input")) return "Input Produk Masuk";
        return "Produk Masuk";

      case "inventory":
        return "Laporan Persediaan";

      case "shipping":
        return "Semua Pengiriman";
      case "reports":
        return "Laporan";

      case "change_password":
        return "Ubah Password";
      case "submissions":
      default:
        return "Semua Pengajuan";
    }
  }, [activeKey, pathname]);

  const go = (to) => {
    navigate(to);
    setSideOpen(false);
  };

  const handleLogout = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (_) {}
    navigate("/login", { replace: true });
  };

  return (
    <>
      <style>{`
/* =================== ADMIN LAYOUT (scoped) =================== */
:root{
  --admin-sb-w: 220px;
  --admin-header-h: 64px;
  --admin-teal: #0ea5a5;
  --admin-bg: #f2f6f5;
  --admin-border: #e9eef2;
}

/* ✅ penting: jangan kunci scroll body tanpa menyediakan scroll area lain */
html, body { height: 100%; }

/* ✅ shell jadi viewport fixed; scroll dipindah ke .admin-main */
.admin-shell{
  height: 100dvh;
  background: var(--admin-bg);
  overflow: hidden; /* ✅ stop body scroll, tapi kita sediakan scroll di main */
}

/* ---------- SIDEBAR ---------- */
.admin-sidenav{
  position: fixed; inset: 0 auto 0 0; z-index: 2500;
  width: var(--admin-sb-w); height: 100vh;
  background: linear-gradient(180deg, var(--admin-teal), #0e9a84 75%);
  color: #eafffb; box-shadow: 0 6px 22px rgba(0,0,0,.08);
  display:flex; flex-direction:column;
}
.admin-sb-head{
  height: 64px; display:flex; align-items:center; gap:10px;
  padding: 0 14px; border-bottom: 1px solid rgba(255,255,255,.18);
}
.admin-sb-logo{
  width: 34px; height: 34px; border-radius: 8px; object-fit: contain;
  background: rgba(255,255,255,.85);
}
.admin-sb-title{
  font-weight: 800; letter-spacing: .02em; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; font-size:14px;
}

.admin-sb-nav{ padding: 10px; overflow:auto; }
.admin-sb-item{
  display:flex; align-items:center; gap:10px;
  padding:10px 12px; border-radius:12px; cursor:pointer; user-select:none;
  color:#e6fffa; opacity:.96;
}
.admin-sb-item:hover{ background: rgba(255,255,255,.12); }
.admin-sb-item[data-active="true"]{
  background: rgba(255,255,255,.18);
  box-shadow: inset 0 0 0 2px rgba(255,255,255,.25);
  font-weight:700;
}
.admin-sb-ic{ font-size:16px; min-width:16px; }
.admin-sb-tx{ font-size:14px; }

/* --- Grup + Submenu LOG --- */
.admin-sb-group{ margin-top: 6px; }
.admin-sb-group-head{
  display:flex; align-items:center; justify-content:space-between;
  padding:10px 12px; border-radius:12px; cursor:pointer; user-select:none;
  color:#e6fffa; gap:10px;
}
.admin-sb-group-head:hover{ background: rgba(255,255,255,.12); }
.admin-sb-group-head[data-open="true"]{ background: rgba(255,255,255,.16); }
.admin-sb-group-title{ display:flex; align-items:center; gap:10px; font-size:14px; font-weight:700; }
.admin-sb-chevron{ transition: transform .18s ease; }
.admin-sb-group-head[data-open="true"] .admin-sb-chevron{ transform: rotate(180deg); }

.admin-sb-sub{
  overflow:hidden; transition: grid-template-rows .22s ease, padding .22s ease;
  display:grid; grid-template-rows: 0fr; padding: 0 4px;
}
.admin-sb-sub[data-open="true"]{ grid-template-rows: 1fr; padding: 6px 4px 8px; }
.admin-sb-sub-inner{ min-height: 0; display:flex; flex-direction:column; gap:6px; }

.admin-sb-subitem{
  display:flex; align-items:center; gap:8px;
  padding:8px 10px; border-radius:10px; cursor:pointer;
  color:#e6fffa; opacity:.95; margin-left: 26px;
  background: transparent;
}
.admin-sb-subitem:hover{ background: rgba(255,255,255,.12); }
.admin-sb-subitem[data-active="true"]{
  background: rgba(255,255,255,.20);
  box-shadow: inset 0 0 0 2px rgba(255,255,255,.22);
  font-weight:700;
}

/* overlay mobile */
.admin-overlay{ display:none; }
@media (max-width: 992px){
  .admin-sidenav{
    transform: translateX(-100%);
    width: min(84vw, 300px);
    transition: transform .22s ease;
  }
  .admin-sidenav[data-open="true"]{ transform: translateX(0); }
  .admin-overlay{
    display:block; position:fixed; inset:0; z-index:2400;
    background: rgba(0,0,0,.28);
    opacity:0; pointer-events:none; transition: opacity .2s ease;
  }
  .admin-overlay[data-open="true"]{ opacity:1; pointer-events:auto; }
}

/* ---------- HEADER ---------- */
.admin-header{
  position: sticky; top:0; z-index: 1200;
  height: var(--admin-header-h);
  background:#fff; border-bottom: 1px solid var(--admin-border);
  display:grid; grid-template-columns: auto 1fr auto; align-items:center;
  gap: 12px; padding: 10px 14px;
}

.admin-left{ display:flex; align-items:center; gap:10px; }
.admin-logo{ height: 28px; width:auto; object-fit:contain; }
.admin-burger{
  display:none; place-items:center;
  width:36px; height:36px; border-radius:10px;
  border:1px solid var(--admin-border); background:#fff; cursor:pointer;
}
@media (max-width: 992px){ .admin-burger{ display:grid; } }

.admin-title{
  margin:0; text-align:center; font-weight:800; letter-spacing:.02em;
  font-size:18px; color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}

.admin-right{ display:flex; align-items:center; gap:10px; }
.admin-logout{
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:999px; border:none;
  background:#ef4444; color:#fff; font-weight:800; cursor:pointer;
}
.admin-logout:hover{ filter:brightness(.95); }

/* ---------- MAIN (HEADER + CONTENT SCROLL AREA) ---------- */
/* ✅ ini kunci perbaikannya:
   main punya height penuh viewport dan jadi tempat scroll.
*/
.admin-main{
  height: 100dvh;
  display: flex;
  flex-direction: column;
  margin-left: var(--admin-sb-w);
  min-width: 0;         /* ✅ penting untuk horizontal scroll tabel */
  overflow: hidden;     /* header sticky tetap aman */
}

@media (max-width: 992px){
  .admin-main{ margin-left: 0; }
}

/* Konten scroll (outer scrollbar) */
.admin-content{
  flex: 1;
  overflow-y: auto;     /* ✅ outer scrollbar aktif di sini */
  overflow-x: hidden;
  padding: 16px;
  min-width: 0;
}

/* Container halaman: buat lebih fleksibel agar tabel tidak sempit */
.admin-container{
  max-width: 1400px;    /* sebelumnya 1120px -> terlalu sempit untuk tabel */
  width: 100%;
  margin: 0 auto;
  min-width: 0;
}

/* Opsional: smoothing scroll + agar scrollbar stabil */
.admin-content{
  scroll-behavior: smooth;
  scrollbar-gutter: stable both-edges;
}
      `}</style>

      {/* BACKDROP (mobile) */}
      <div
        className="admin-overlay"
        data-open={sideOpen ? "true" : "false"}
        onClick={() => setSideOpen(false)}
      />

      {/* SIDEBAR */}
      <aside className="admin-sidenav" data-open={sideOpen ? "true" : "false"}>
        <div className="admin-sb-head">
          {logoSipedas ? (
            <img src={logoSipedas} alt="SIPEDAS" className="admin-sb-logo" />
          ) : (
            <div className="admin-sb-logo" />
          )}
          <div className="admin-sb-title">SIPEDAS</div>
        </div>

        <nav className="admin-sb-nav">
          <div className="admin-sb-item" data-active={activeKey === "users"} onClick={() => go("/admin/users")}>
            <span className="admin-sb-ic"><FaUsers /></span>
            <span className="admin-sb-tx">Pengguna</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "categories"} onClick={() => go("/admin/categories")}>
            <span className="admin-sb-ic"><FaTags /></span>
            <span className="admin-sb-tx">Kategori</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "products"} onClick={() => go("/admin/products")}>
            <span className="admin-sb-ic"><FaBoxOpen /></span>
            <span className="admin-sb-tx">Produk</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "incoming"} onClick={() => go("/admin/produk-masuk")}>
            <span className="admin-sb-ic"><FaArrowDown /></span>
            <span className="admin-sb-tx">Produk Masuk</span>
          </div>

          <div
            className="admin-sb-item"
            data-active={activeKey === "inventory"}
            onClick={() => go("/admin/laporan-persediaan")}
          >
            <span className="admin-sb-ic"><FaClipboardCheck /></span>
            <span className="admin-sb-tx">Laporan Persediaan</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "submissions"} onClick={() => go("/admin/submissions")}>
            <span className="admin-sb-ic"><FaClipboardList /></span>
            <span className="admin-sb-tx">Pengajuan</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "shipping"} onClick={() => go("/admin/pengiriman")}>
            <span className="admin-sb-ic"><FaTruck /></span>
            <span className="admin-sb-tx">Pengiriman</span>
          </div>

          <div className="admin-sb-item" data-active={activeKey === "reports"} onClick={() => go("/admin/reports")}>
            <span className="admin-sb-ic"><FaChartBar /></span>
            <span className="admin-sb-tx">Laporan</span>
          </div>

          <div
            className="admin-sb-item"
            data-active={activeKey === "change_password"}
            onClick={() => go("/admin/change-password")}
          >
            <span className="admin-sb-ic"><FaKey /></span>
            <span className="admin-sb-tx">Ubah Password</span>
          </div>
        </nav>
      </aside>

      {/* ✅ SHELL + MAIN (ini perubahannya) */}
      <div className="admin-shell">
        <main className="admin-main">
          {/* HEADER */}
          <header className="admin-header">
            <div className="admin-left">
              <button className="admin-burger" onClick={() => setSideOpen(true)} aria-label="Buka menu">
                <FaBars />
              </button>
              <img src={logoKemenkes} alt="Kemenkes BKK Pangkalpinang" className="admin-logo" />
            </div>

            <h1 className="admin-title">{headerTitle}</h1>

            <div className="admin-right">
              <button className="admin-logout" onClick={handleLogout}>
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </header>

          {/* KONTEN (outer scroll di sini) */}
          <div className="admin-content">
            <div className="admin-container">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
